import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCookie } from 'hooks/useCookie';
import PropTypes from 'prop-types';
import { useHash } from 'react-use';

import { STORAGE_KEY } from 'global/constants';

import { isTokenExpired, parseAccessToken, tryRedirect } from 'utils/AuthUtils';
import { setCookie } from 'utils/CookieUtils';
import { getAuthCIAMUrl, getAuthEIAMUrl, getHomepageUrlByTenantId } from 'utils/UrlUtil';
import { getTenantId } from 'utils/tenantUtil';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const Auth = ({ children, code, isCIAM, isPublic }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useCookie(STORAGE_KEY.JWT, '');
  const [hash] = useHash();
  const handledCodeRef = useRef(null);


  const getAuthUrl = useCallback(() => {
    if (isCIAM) {
      return getAuthCIAMUrl();
    }
    return getAuthEIAMUrl();
  }, [isCIAM]);

  const getJWTToken = useCallback(
    async (jwtCode) => {
      const response = await fetch(getAuthUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authCode: jwtCode,
          tenantId: getTenantId(),
          redirectUrl: isCIAM
            ? getHomepageUrlByTenantId()
            : `${getHomepageUrlByTenantId()}?auth=EIAM`,
        }),
      });
      return response.json();
    },
    [getAuthUrl, isCIAM]
  );

  const handleCode = useCallback(
    async (jwtCode) => {
      try {
        const tokenEON = await getJWTToken(jwtCode);
        if (tokenEON?.jwtToken) {
          setToken(tokenEON.jwtToken);
          return tokenEON.jwtToken;
        }
        throw new Error('No JWT token returned');
      } catch (err) {
        console.error('Error in handleCode', err);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
      return null;
    },
    [getJWTToken, setToken]
  );

  useEffect(() => {
    setCookie(STORAGE_KEY.IAM, isCIAM ? 'CIAM' : 'EIAM', 7);
    const verify = async () => {
      if (isPublic) {
        setIsAuthenticated(true);
        setIsLoading(false);
      } else if (code) {
        if (handledCodeRef.current === code) {
          return;
        }
        handledCodeRef.current = code;
        const jwtToken = await handleCode(code);
        if (!jwtToken) {
          return;
        }
        tryRedirect();
        setIsAuthenticated(true);
        setIsLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (token && !isTokenExpired(token)) {
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        const parsedToken = parseAccessToken(hash);
        if (parsedToken) {
          setToken(parsedToken);
        } else {
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      }
    };

    verify();
  }, [hash, token, setToken, handleCode, code, isCIAM, isPublic]);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      setIsAuthenticated,
      token,
      isCIAM,
    }),
    [isAuthenticated, isLoading, setIsAuthenticated, token, isCIAM]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
Auth.propTypes = {
  children: PropTypes.node.isRequired,
  code: PropTypes.string,
  isCIAM: PropTypes.bool,
  isPublic: PropTypes.bool,
};
Auth.defaultProps = {
  code: '',
  isCIAM: true,
  isPublic: false,
};
